import { useEffect, useId, useMemo, useState } from 'react';

import { adminSearchUsers } from '../api/admin';
import { parseJsonSafe } from '../api/client';

function normalizeUser(item) {
  if (!item || item.id == null) {
    return null;
  }
  return {
    id: Number(item.id),
    email: String(item.email || ''),
  };
}

function UserSearchSelect({
  label,
  placeholder,
  role,
  multiple = false,
  selected,
  onChange,
  activeOnly = true,
  limit = 10,
  helpText,
}) {
  const inputId = useId();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const selectedItems = useMemo(() => {
    if (multiple) {
      return Array.isArray(selected)
        ? selected
            .map((item) => normalizeUser(item))
            .filter((item) => item !== null)
        : [];
    }
    const normalized = normalizeUser(selected);
    return normalized ? [normalized] : [];
  }, [multiple, selected]);

  const selectedIdSet = useMemo(
    () => new Set(selectedItems.map((item) => Number(item.id))),
    [selectedItems]
  );

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setIsSearching(false);
      setSearchFailed(false);
      setIsOpen(false);
      setHighlightedIndex(-1);
      return undefined;
    }

    let cancelled = false;
    setIsSearching(true);
    setSearchFailed(false);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await adminSearchUsers({
          q: trimmed,
          role,
          active_only: activeOnly,
          limit,
        });
        const data = await parseJsonSafe(response);
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          setResults([]);
          setSearchFailed(true);
          setIsOpen(false);
          setHighlightedIndex(-1);
          return;
        }
        const rawItems = Array.isArray(data) ? data : [];
        const normalizedItems = rawItems
          .map((item) => normalizeUser(item))
          .filter((item) => item !== null)
          .filter((item) => !selectedIdSet.has(Number(item.id)));
        setResults(normalizedItems);
        setIsOpen(true);
        setHighlightedIndex(normalizedItems.length > 0 ? 0 : -1);
      } catch (_error) {
        if (cancelled) {
          return;
        }
        setResults([]);
        setSearchFailed(true);
        setIsOpen(false);
        setHighlightedIndex(-1);
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [activeOnly, limit, query, role, selectedIdSet]);

  const handleSelect = (item) => {
    const normalized = normalizeUser(item);
    if (!normalized) {
      return;
    }

    if (multiple) {
      if (selectedIdSet.has(normalized.id)) {
        return;
      }
      onChange([...(Array.isArray(selectedItems) ? selectedItems : []), normalized]);
    } else {
      onChange(normalized);
    }

    setQuery('');
    setResults([]);
    setSearchFailed(false);
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleRemove = (userId) => {
    if (multiple) {
      onChange(selectedItems.filter((item) => Number(item.id) !== Number(userId)));
      return;
    }
    onChange(null);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }

    if (!isOpen || results.length === 0) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev + 1;
        return next >= results.length ? 0 : next;
      });
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        const next = prev - 1;
        return next < 0 ? results.length - 1 : next;
      });
      return;
    }

    if (event.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < results.length) {
      event.preventDefault();
      handleSelect(results[highlightedIndex]);
    }
  };

  return (
    <div className="stack-sm">
      <label htmlFor={inputId}>{label}</label>
      <input
        id={inputId}
        type="text"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (results.length > 0) {
            setIsOpen(true);
          }
        }}
        placeholder={placeholder}
      />
      {helpText ? <p className="muted-text">{helpText}</p> : null}
      {isSearching ? <p className="muted-text">Searching...</p> : null}
      {!isSearching && searchFailed ? <p className="muted-text">Search failed</p> : null}
      {!isSearching && isOpen && !searchFailed && query.trim() && results.length === 0 ? (
        <p className="muted-text">No matches</p>
      ) : null}
      {!isSearching && isOpen && results.length > 0 ? (
        <ul className="stack-sm" role="listbox" aria-label={`${label} search results`}>
          {results.map((item, index) => (
            <li key={`${role}-${item.id}`} className="notice" role="option" aria-selected={index === highlightedIndex}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setHighlightedIndex(index)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleSelect(item);
                  }
                  if (event.key === 'Escape') {
                    event.preventDefault();
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                  }
                }}
              >
                {item.email} (id: {item.id})
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {multiple ? (
        selectedItems.length > 0 ? (
          <ul className="stack-sm">
            {selectedItems.map((item) => (
              <li key={`selected-${role}-${item.id}`} className="notice">
                {item.email} (id: {item.id}){' '}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleRemove(item.id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null
      ) : selectedItems[0] ? (
        <p className="muted-text">
          Selected: {selectedItems[0].email} (id: {selectedItems[0].id}){' '}
          <button type="button" className="btn btn-secondary" onClick={() => handleRemove(selectedItems[0].id)}>
            Clear
          </button>
        </p>
      ) : null}
    </div>
  );
}

export default UserSearchSelect;
